import {
  KgInsight,
  KgInsightQuotesSection,
  KgInsightSentiment,
} from '@api/api/kg-insights';
import {
  GradientCard,
  type GradientCardAccent,
} from '@design/components/gradient-card/GradientCard';
import { SENTIMENT_BADGE_COLOR } from '@terminal/modules/reflexivity-graph/kg-insight-constants';
import { findSection } from '@terminal/widgets/catalyst-insights-drawer/catalyst-widget/components/kg-insight-content/utils/kg-insight-content-utils';
import { useTranslation } from 'react-i18next';
import styles from './LeadershipQuotes.module.scss';

export type LeadershipQuotesRow = {
  frame: string;
  quote: string;
  author: string;
  sentiment: KgInsightSentiment;
};

export type LeadershipQuotesProps = {
  kgInsight: KgInsight;
};

const SENTIMENT_ACCENT: Record<KgInsightSentiment, GradientCardAccent> = {
  positive: 'success',
  negative: 'error',
  neutral: 'warning',
};

const WRAPPING_QUOTE_PATTERN = '[\\s\\p{Pi}\\p{Pf}"\'`]';
const WRAPPING_QUOTES = new RegExp(
  `^${WRAPPING_QUOTE_PATTERN}+|${WRAPPING_QUOTE_PATTERN}+$`,
  'gu'
);

export const LeadershipQuotes = ({ kgInsight }: LeadershipQuotesProps) => {
  const { t } = useTranslation(['insights']);

  const quotesSection = findSection<KgInsightQuotesSection>(
    kgInsight.sections,
    'quotes'
  );

  const rows: LeadershipQuotesRow[] =
    quotesSection?.data.cards.map(card => ({
      frame: card.topic,
      quote: `"${card.quote.replace(WRAPPING_QUOTES, '')}"`,
      author: `${card.speaker.name}, ${card.speaker.title}`,
      sentiment: card.sentiment,
    })) ?? [];

  return (
    <div className={styles.list}>
      {rows.map((row, index) => (
        <GradientCard
          key={`${row.frame}-${index}`}
          eyebrow={row.frame}
          body={row.quote}
          footnote={row.author}
          accent={SENTIMENT_ACCENT[row.sentiment]}
          tagColor={SENTIMENT_BADGE_COLOR[row.sentiment]}
          tagLabel={t('insights:catalystGeneration.sentiment', {
            context: row.sentiment,
          })}
        />
      ))}
    </div>
  );
};
